/**
 * Shared presentational bits for the Control Surfaces workbench
 * (ControlSurfacesSurface): the labeled param slider and the style table.
 * Styles follow the AircraftSurface idiom - inline CSSProperties off the V5
 * tokens plus the theme-aware highlift palette (no hardcoded hexes).
 */
import type { CSSProperties } from "react";

import type { HighliftPalette } from "../highlift/foil/useHighliftTheme";

export function ParamSlider({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
  palette,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (v: number) => void;
  palette: HighliftPalette;
}) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: palette.ink3 }}>
      <span style={{ width: 86, flex: "0 0 auto" }}>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ flex: 1, minWidth: 0 }}
        aria-label={label}
      />
      <span
        style={{
          fontFamily: "var(--v5-font-mono, monospace)",
          color: palette.ink2,
          minWidth: 62,
          textAlign: "right",
        }}
      >
        {display}
      </span>
    </label>
  );
}

export function makeControlSurfaceStyles(palette: HighliftPalette): Record<string, CSSProperties> {
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      background: "var(--v5-surface)",
      color: "var(--v5-ink)",
      font: "13px var(--v5-sans, system-ui)",
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      padding: "8px 12px",
      borderBottom: "1px solid var(--v5-line)",
    },
    title: { fontWeight: 600, fontSize: 14, letterSpacing: 0.2 },
    subtitle: { fontSize: 11.5, color: palette.ink3 },
    stat: { marginLeft: "auto", font: "12px var(--v5-mono, monospace)", opacity: 0.75 },
    body: { display: "flex", flex: 1, minHeight: 0 },
    viewer: { flex: 1, minWidth: 0, position: "relative", background: palette.bg },
    viewerEmpty: {
      position: "absolute",
      inset: 0,
      display: "grid",
      placeItems: "center",
      padding: 24,
      textAlign: "center",
      color: palette.ink3,
      fontSize: 13,
      lineHeight: 1.6,
    },
    emptyState: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      color: palette.ink3,
      textAlign: "center",
      padding: 24,
    },
    panel: {
      width: 380,
      flex: "0 0 auto",
      borderLeft: "1px solid var(--v5-line)",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
    },
    sectionHead: {
      padding: "10px 12px 4px",
      fontWeight: 600,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: palette.ink4,
      borderTop: "1px solid var(--v5-line)",
    },
    section: { display: "flex", flexDirection: "column", gap: 8, padding: "4px 12px 12px" },
    fieldLabel: {
      display: "flex",
      flexDirection: "column",
      gap: 3,
      fontSize: 10.5,
      color: palette.ink4,
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    textInput: {
      background: palette.surface2,
      color: palette.ink,
      border: `1px solid ${palette.line}`,
      borderRadius: "var(--v5-radius, 4px)",
      padding: "4px 7px",
      font: "12px var(--v5-mono, monospace)",
      textTransform: "none",
      letterSpacing: 0,
    },
    primaryButton: {
      background: "var(--v5-primary)",
      color: palette.onPrimary,
      border: "none",
      borderRadius: "var(--v5-radius, 4px)",
      padding: "5px 12px",
      cursor: "pointer",
      font: "inherit",
      fontWeight: 600,
    },
    secondaryButton: {
      background: palette.surface2,
      color: palette.ink,
      border: `1px solid ${palette.lineStrong}`,
      borderRadius: "var(--v5-radius, 4px)",
      padding: "5px 12px",
      cursor: "pointer",
      font: "inherit",
    },
    surfaceRow: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 8,
      padding: "5px 8px",
      borderRadius: 6,
      border: `1px solid ${palette.line}`,
      background: palette.surface,
      color: palette.ink,
      cursor: "pointer",
      font: "inherit",
      textAlign: "left",
    },
    surfaceRowActive: {
      border: `1px solid ${palette.primary}`,
      background: palette.primarySoft,
    },
    rowMeta: { font: "10.5px var(--v5-mono, monospace)", color: palette.ink4 },
    noteText: { fontSize: 11, color: palette.ink3, lineHeight: 1.5 },
    errorText: { fontSize: 11, color: palette.warn, lineHeight: 1.5 },
    footNote: { fontSize: 10, color: palette.ink4, lineHeight: 1.5 },
  };
}
